import React, { useContext, useState } from 'react'
import classNames from 'classnames'
import ConversationStarter from '#src/components/ConversationStarter'
import AuthenticationFlowModal from '#src/components/AuthenticationFlowModal'
import { AuthIntent } from '#src/components/AuthenticationFlow'
import { IdentityContext } from '#src/containers/Identity'
import { ChatWidget } from '@deal/chat-firebase'
import { useStartExpertConsumerConversationMutation } from '../../mutations/StartExpertConsumerConversation.generated'
import { useChatWidgetExpertQuery } from './GetChatWidgetExpert.generated'
import {
  ConversationDescriptor,
  FirebaseConversationDescriptor,
  LocalConversationDescriptor
} from '#src/hooks/useConversation'
import belowBreakpoint from '#src/hocs/belowBreakpoint'
import clientOnly from '#src/hocs/clientOnly'
import { useHistory } from 'react-router'
import { isExpertPresent } from '#src/services/presence'
import { UserAgentContext } from '#src/containers/UserAgent'
import styles from './styles.css'
import DeactivatedExpertChatBanner from '#src/components/DeactivatedExpertChatBanner'

export interface ChatWidgetProps {
  conversation: ConversationDescriptor
  className?: string
  collapsedClassName?: string
}

const ChatWidgetContainer: React.FC<ChatWidgetProps> = ({
  conversation,
  className,
  collapsedClassName
}) => {
  // Start a conversation between an expert and a consumer
  const { data: expertData } = useChatWidgetExpertQuery({
    variables: {
      id: (conversation as FirebaseConversationDescriptor | LocalConversationDescriptor).expertId
    },
    // Only Firebase and Local conversations havve an expertId to query by. If the conversation
    //  is loading or no experts were available, we skip this query.
    skip: conversation.type !== 'FirebaseConversation' && conversation.type !== 'LocalConversation'
  })

  // Start a conversation between an expert and a consumer
  const [
    startExpertConsumerConversation,
    startExpertConsumerConversationData
  ] = useStartExpertConsumerConversationMutation()

  const { myself } = useContext(IdentityContext)
  const history = useHistory()

  // Authentication state
  const [authIntent, setAuthIntent] = useState(AuthIntent.Register)
  const [showRegistrationModal, setShowRegistrationModal] = useState(false)

  // We don't want to show the chat to bots because they index this on every single page
  // which dings our rankings because of duplicate content
  const userAgent = useContext(UserAgentContext)

  if (userAgent?.isBot) {
    return null
  }

  // Do not render the chat widget in any of the follow cases:
  //
  //  1. We are still loading which conversation to render (hidden is the desired loading state)
  //  2. There are no experts available
  //  3. You have been assigned an expert but their profile info is still loading
  if (
    conversation.type === 'Loading' ||
    conversation.type === 'NoExpertsAvailable' ||
    !expertData
  ) {
    return null
  }

  const expert = expertData.businessUser

  // Visually hide the chat widget if the cart or mobile browse menu is expanded
  const isCartOpen = history.location.hash === '#cart'
  let zindexClass = ''
  if (
    isCartOpen ||
    history.location.state?.isBrowseExpanded ||
    history.location.state?.isSearchExpanded
  ) {
    zindexClass = styles.pushedBack
  }

  // Logged-in users can initiate conversations with experts by messaging them directly.
  const handleStartConversation = () => {
    startExpertConsumerConversation({
      variables: {
        input: {
          // The `onConversationStarted` callback will only ever by triggered
          //   if the user is logged-in.
          consumerId: myself!.id,
          expertId: expert.id,
          sourceFlow: {
            directExpertChatFlow: {
              expertId: expert.id
            }
          }
        }
      }
    })
  }

  // Some features -- namely the conversation starters -- require a user to register as
  //  part of the flow.
  const handleAuthenticationRequested = () => {
    setShowRegistrationModal(true)
  }

  // Use the passed-in conversation ID, if one exists. Otherwise, see if we've created one
  //   via `startExpertConsumerConversation`.
  const conversationId: string | undefined =
    conversation.type === 'FirebaseConversation' && conversation.conversationId
      ? conversation.conversationId
      : startExpertConsumerConversationData.data?.startExpertConsumerConversation.conversation?.id

  return (
    <>
      <AuthenticationFlowModal
        isOpen={showRegistrationModal}
        intent={authIntent}
        onIntentChanged={setAuthIntent}
        onRegister={() => {}}
        onLogin={() => {}}
        onLogout={() => {}}
        standalone={true}
        swapLoginSignupOrder={true}
        onRequestClose={() => setShowRegistrationModal(false)}
        headline={
          authIntent === AuthIntent.Register
            ? `Take a few seconds to create an account, so we can match you with an expert`
            : undefined
        }
      />
      <ChatWidget
        userId={expert.id}
        conversationId={conversationId}
        online={isExpertPresent(expert)}
        history={history}
        getAppRoot={() => document.querySelector('#deal')}
        collapsedClassName={collapsedClassName}
        className={classNames(className, zindexClass)}
        onConversationStarted={handleStartConversation}
        onAuthenticationRequested={handleAuthenticationRequested}
        guestChatEnabled={conversation.type === 'LocalConversation'}
        bottomBanner={() => {
          if (myself && expert && conversationId) {
            return (
              <DeactivatedExpertChatBanner
                consumerName={myself?.user.firstName || ''}
                expertName={expert.shortName || ''}
                expertState={expert.state}
                conversationId={conversationId}
                placeHolderExpertProfilePhotoUrl={
                  expert.department?.placeholderExpert?.expertProfile?.profilePhoto?.url
                }
                placeHolderExpertName={expert.department.placeholderExpert.shortName}
              />
            )
          } else {
            return null
          }
        }}
      >
        <ConversationStarter expert={expert} />
      </ChatWidget>
    </>
  )
}

export default clientOnly(belowBreakpoint('lg', ChatWidgetContainer))
